/*
 * PreProduct pre-order ship-date badge.
 *
 * PreProduct's storefront script runs wherever pre-order buttons appear (product
 * pages and collection cards) and exposes each listing client-side via the
 * `data-pre-order-btn-vis` callback's `context.formattedListing`. We read the
 * release / ship date from there and fill the server-rendered `[data-preorder-date]`
 * element inside the `[data-preorder-badge]` overlay.
 *
 * Sold-out pre-order products keep a visible button labelled "Sold out" (not
 * "Pre-order") and use the theme's sold-out button styling. PreProduct may rewrite
 * the label after load, so we re-apply the sold-out state whenever needed.
 *
 * Wiring (in Liquid): add `data-pre-order-btn-vis="ppShipDate"` (and
 * `data-starting-variant="<variant id>"`) to the pre-order buttons.
 */
(function () {
  'use strict';

  function formatDate(date) {
    try {
      return date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (e) {
      return date.toISOString().slice(0, 10);
    }
  }

  function getSoldOutLabel() {
    return (window.variantStrings && window.variantStrings.soldOut) || 'Out of Stock';
  }

  /*
   * PreProduct's formattedListing carries the shipping statement in one of two forms:
   *   - different_lead_time: a free-text statement the merchant typed, e.g. "May 2027"
   *   - lead_time: a day count that automatically counts down, e.g. 335
   * Prefer the merchant's typed statement; otherwise derive a target date from the
   * remaining lead-time days (today + lead_time).
   */
  function extractShipDisplay(listing) {
    if (!listing || typeof listing !== 'object') return null;

    if (typeof listing.different_lead_time === 'string' && listing.different_lead_time.trim()) {
      return listing.different_lead_time.trim();
    }

    if (typeof listing.lead_time === 'number' && listing.lead_time > 0) {
      var target = new Date();
      target.setDate(target.getDate() + listing.lead_time);
      return formatDate(target);
    }

    return null;
  }

  function isSoldOutButton(button) {
    if (!button) return false;
    if (button.hasAttribute('data-sold-out-state')) return true;
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return true;
    if (button.classList.contains('button--sold-out')) return true;

    var form = button.closest('product-form');
    if (form) {
      var variantInput = form.querySelector('.product-variant-id[disabled]');
      if (variantInput) return true;
    }

    return false;
  }

  function applySoldOutState(button) {
    if (!button || button.getAttribute('data-sold-out-duplicate') === 'true') return;

    button.classList.remove('button--preorder', 'button--add-to-cart');
    button.classList.add('button--sold-out');
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.hidden = false;
    button.style.display = '';

    var soldOutText = getSoldOutLabel();
    var soldOutMessage = button.querySelector('.sold-out-message');
    if (soldOutMessage) soldOutMessage.remove();

    var labels = button.querySelectorAll('span:not(.icon-wrap):not(.loading__spinner)');
    var keptOne = false;
    labels.forEach(function (span) {
      if (!keptOne) {
        span.textContent = soldOutText;
        span.classList.remove('hidden');
        keptOne = true;
      } else {
        span.remove();
      }
    });

    if (!keptOne) {
      button.textContent = soldOutText;
    }
  }

  function isThemeProductButton(button) {
    return !!(button && button.id && button.id.indexOf('ProductSubmitButton-') === 0);
  }

  function showSoldOutButton(button) {
    if (!button) return;
    button.removeAttribute('data-sold-out-duplicate');
    button.hidden = false;
    button.style.removeProperty('display');
    button.removeAttribute('aria-hidden');
    applySoldOutState(button);
  }

  function hideSoldOutDuplicate(button) {
    if (!button || isThemeProductButton(button)) return;
    button.setAttribute('data-sold-out-duplicate', 'true');
    button.hidden = true;
    button.style.setProperty('display', 'none', 'important');
    button.setAttribute('aria-hidden', 'true');
  }

  function productFormCanonical(scope) {
    if (!scope || !scope.querySelector) return null;
    return scope.querySelector('[id^="ProductSubmitButton-"][data-sold-out-state]');
  }

  function collect(scope, selector) {
    var nodes = [];
    if (!scope) return nodes;
    if (scope.matches && scope.matches(selector)) nodes.push(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll(selector).forEach(function (node) {
        nodes.push(node);
      });
    }
    return nodes;
  }

  // PreProduct can inject a second CTA on sold-out products. Keep the theme button
  // and hide only an extra button sitting in the same button group.
  function dedupeSoldOutButtons(root) {
    var scope = root || document;

    collect(scope, '.product-card-wrapper .card').forEach(function (card) {
      var canonical = card.querySelector('[data-sold-out-state]');
      if (!canonical) return;

      applySoldOutState(canonical);
      card
        .querySelectorAll('.card__content .quick-add__submit, .card__content [data-native-pre-order-btn]')
        .forEach(function (btn) {
          if (btn === canonical || canonical.contains(btn)) return;
          hideSoldOutDuplicate(btn);
        });
    });

    collect(scope, '.product-form__buttons').forEach(function (group) {
      var canonical = productFormCanonical(group);
      if (!canonical) return;

      showSoldOutButton(canonical);
      group
        .querySelectorAll('.product-form__submit, [data-native-pre-order-btn], .shopify-payment-button')
        .forEach(function (btn) {
          if (btn === canonical || canonical.contains(btn)) return;
          hideSoldOutDuplicate(btn);
        });
    });
  }

  function soldOutHost(button) {
    if (!button || !button.closest) return null;
    return button.closest('.card') || button.closest('.product__info-container') || button.closest('.product');
  }

  // The button (in the product form / card content) and the badge (overlaid on the
  // media) live in different sub-trees, so walk up to a shared container and search
  // down from there. Card scopes are tried first so a collection grid matches the
  // correct card before falling back to the single-product PDP container.
  function findBadge(button) {
    if (!button || typeof button.closest !== 'function') return null;
    var scopes = ['.card-wrapper', '.card', '.product', 'main'];
    for (var i = 0; i < scopes.length; i++) {
      var scope = button.closest(scopes[i]);
      if (scope) {
        var badge = scope.querySelector('[data-preorder-badge]');
        if (badge) return badge;
      }
    }
    return null;
  }

  function updateBadge(badge, display) {
    if (!badge || !display) return;
    var dateEl = badge.querySelector('[data-preorder-date]');
    if (!dateEl) return;
    if (dateEl.getAttribute('data-preorder-filled') === display) return;
    dateEl.textContent = display;
    dateEl.setAttribute('data-preorder-filled', display);
  }

  function syncSoldOutButtons(root) {
    dedupeSoldOutButtons(root || document);
  }

  window.ppShipDate = function (button, show, context) {
    try {
      if (isSoldOutButton(button)) {
        if (isThemeProductButton(button)) {
          showSoldOutButton(button);
          dedupeSoldOutButtons(button.closest('.product-form__buttons') || document);
          return;
        }

        var soldOutScope = soldOutHost(button);
        var canonicalSoldOut = soldOutScope && soldOutScope.querySelector('[data-sold-out-state]');
        if (canonicalSoldOut && canonicalSoldOut !== button && !isThemeProductButton(canonicalSoldOut)) {
          canonicalSoldOut = soldOutScope.querySelector('[id^="ProductSubmitButton-"][data-sold-out-state]') || canonicalSoldOut;
        }
        if (canonicalSoldOut && canonicalSoldOut !== button) {
          hideSoldOutDuplicate(button);
          dedupeSoldOutButtons(soldOutScope);
          return;
        }
        applySoldOutState(button);
        dedupeSoldOutButtons(soldOutScope || document);
        return;
      }

      if (!show) {
        if (button && button.closest && button.closest('.card--preorder')) {
          var card = button.closest('.card');
          var canonical = card && card.querySelector('[data-sold-out-state]');
          if (canonical && canonical !== button) {
            hideSoldOutDuplicate(button);
            dedupeSoldOutButtons(card);
            return;
          }
          applySoldOutState(button);
          dedupeSoldOutButtons(card || document);
          return;
        }

        if (button && button.style) {
          button.style.display = 'none';
        }
        return;
      }

      if (button) {
        button.hidden = false;
        button.disabled = false;
        if (button.style) button.style.display = '';
      }

      var listing = context && context.formattedListing;
      var display = extractShipDisplay(listing);
      var badge = findBadge(button);
      if (badge && display) updateBadge(badge, display);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[ppShipDate] error', e);
    }
  };

  document.addEventListener('DOMContentLoaded', function () {
    syncSoldOutButtons();
  });

  window.addEventListener('load', function () {
    syncSoldOutButtons();
    window.setTimeout(syncSoldOutButtons, 500);
    window.setTimeout(syncSoldOutButtons, 1500);
  });
})();
